import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Download, Upload, Database, AlertTriangle, CheckCircle, Loader2, HardDrive, Clock, RefreshCw } from 'lucide-react';

const fmtBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};
const fmtWhen = (s) => { const d = new Date(s); return isNaN(d) ? String(s || '—') : d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); };

export default function Settings() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [wipeFirst, setWipeFirst] = useState(false);
  const [backups, setBackups] = useState(null);
  const [backingUp, setBackingUp] = useState(false);

  const fetchBackups = () => api.get('/admin/auto-backups').then(res => setBackups(res.data || [])).catch(() => setBackups([]));

  useEffect(() => {
    api.get('/admin/data-stats')
      .then(res => setStats(res.data))
      .catch(() => toast.error('Failed to load data stats'))
      .finally(() => setLoading(false));
    fetchBackups();
  }, []);

  const handleBackupNow = async () => {
    setBackingUp(true);
    try {
      const res = await api.post('/admin/backup-now');
      toast.success(res.data.message || 'Backup created');
      fetchBackups();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backup failed');
    } finally { setBackingUp(false); }
  };

  const handleDownloadBackup = async (b) => {
    try {
      const res = await api.get(`/admin/auto-backups/${b.backup_id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = (b.storage_path || '').split('/').pop() || `${b.backup_id}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/export-data');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `anmol-metcomp-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${res.data.total_documents} documents`);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a .json file');
      return;
    }
    const confirmMsg = wipeFirst
      ? '⚠ FULL RESTORE: every collection in the backup will be WIPED and replaced so this server exactly mirrors the backup. Records that exist here but not in the backup will be permanently deleted. This cannot be undone. Continue?'
      : 'This will import/update data from the backup file. Existing records with matching IDs will be overwritten; other records are kept. Continue?';
    if (!window.confirm(confirmMsg)) {
      e.target.value = '';
      return;
    }

    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', wipeFirst ? 'replace' : 'merge');
      const res = await api.post('/admin/import-data', formData);
      setImportResult(res.data);
      toast.success(res.data.message);
      // Refresh stats
      const statsRes = await api.get('/admin/data-stats');
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const COLL_LABELS = {
    materials: { label: 'Raw Materials', color: 'text-cyan-500' },
    fg_parts: { label: 'FG Parts', color: 'text-orange-500' },
    customers: { label: 'Customers', color: 'text-emerald-500' },
    alerts: { label: 'Alerts', color: 'text-red-400' },
    customer_documents: { label: 'Documents', color: 'text-violet-400' },
    parser_templates: { label: 'Parser Templates', color: 'text-sky-400' },
    market_reviews: { label: 'Market Reviews', color: 'text-amber-400' },
    users: { label: 'Users', color: 'text-slate-400' },
  };

  const totalDocs = stats ? Object.entries(stats).reduce((sum, [, v]) => sum + v, 0) : 0;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h2 className="font-heading font-bold text-slate-900 text-xl">Settings</h2>
        <p className="text-slate-500 text-sm mt-0.5">Data management and backup</p>
      </div>

      {/* Data Overview */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <Database size={18} className="text-slate-700" />
          <h3 className="font-heading font-bold text-slate-800">Database Overview</h3>
          <span className="text-xs font-mono text-slate-400 ml-auto">{totalDocs} total documents</span>
        </div>

        {loading ? (
          <div className="py-8 text-center text-slate-400">
            <Loader2 className="animate-spin mx-auto mb-2" size={20} />
            Loading stats...
          </div>
        ) : stats ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(COLL_LABELS).map(([key, { label, color }]) => (
              <div key={key} className="bg-slate-50 border border-slate-100 rounded-sm px-4 py-3">
                <p className={`text-xs font-bold uppercase tracking-wider ${color}`}>{label}</p>
                <p className="font-mono font-black text-2xl text-slate-800 mt-1">{stats[key] || 0}</p>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Export / Import */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Export */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-2.5 mb-3">
            <Download size={18} className="text-emerald-600" />
            <h3 className="font-heading font-bold text-slate-800">Export Data</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Download all data (parts, materials, customers, alerts, templates) as a JSON backup file.
            Use this to migrate data to a new deployment.
          </p>
          <button onClick={handleExport} disabled={exporting} data-testid="export-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-emerald-700 transition-colors disabled:opacity-50 shadow-sm">
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            {exporting ? 'Exporting...' : 'Download Backup'}
          </button>
        </div>

        {/* Import */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-2.5 mb-3">
            <Upload size={18} className="text-blue-600" />
            <h3 className="font-heading font-bold text-slate-800">Import Data</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Upload a previously exported JSON backup to restore data.
            {wipeFirst
              ? ' Full restore: matching collections are wiped first so this server exactly mirrors the backup.'
              : ' Existing records with matching IDs will be updated; new records will be created; other records are kept.'}
          </p>

          <label className={`flex items-start gap-2.5 mb-4 p-3 rounded-sm border cursor-pointer transition-colors ${
            wipeFirst ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
          }`}>
            <input type="checkbox" checked={wipeFirst} onChange={e => setWipeFirst(e.target.checked)}
              disabled={importing} data-testid="wipe-first-toggle"
              className="mt-0.5 h-4 w-4 accent-red-600" />
            <span className="text-sm">
              <span className={`font-bold ${wipeFirst ? 'text-red-700' : 'text-slate-700'}`}>
                Wipe existing data first (full restore / mirror)
              </span>
              <span className="block text-xs text-slate-500 mt-0.5">
                Deletes records that aren't in the backup so this server becomes an exact copy.
                Leave off to merge (safer). Never touches user accounts.
              </span>
            </span>
          </label>

          <label className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-bold uppercase tracking-wider rounded-sm transition-colors cursor-pointer shadow-sm ${
            wipeFirst ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
          }`}
            data-testid="import-btn">
            {importing ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {importing ? 'Importing...' : (wipeFirst ? 'Wipe & Restore' : 'Upload Backup')}
            <input type="file" accept=".json" onChange={handleImport} className="hidden" disabled={importing} />
          </label>
        </div>
      </div>

      {/* Automatic Backups */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6" data-testid="auto-backups-card">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <HardDrive size={18} className="text-slate-600" />
            <div>
              <h3 className="font-heading font-bold text-slate-800">Automatic Backups</h3>
              <p className="text-xs text-slate-500">Runs automatically every 24 hours to secure storage. You can also back up on demand.</p>
            </div>
          </div>
          <button onClick={handleBackupNow} disabled={backingUp} data-testid="backup-now-btn"
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-slate-800 disabled:opacity-50 transition-colors">
            {backingUp ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            {backingUp ? 'Backing up…' : 'Back up now'}
          </button>
        </div>

        {backups === null ? (
          <p className="text-sm text-slate-400 py-4">Loading backups…</p>
        ) : backups.length === 0 ? (
          <p className="text-sm text-slate-400 py-4">No backups yet — the first auto-backup runs within a day, or click “Back up now”.</p>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-100 rounded-sm">
            {backups.map(b => (
              <div key={b.backup_id} className="flex items-center justify-between gap-3 px-3 py-2.5" data-testid={`backup-${b.backup_id}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <Clock size={14} className="text-slate-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{fmtWhen(b.created_at)}</p>
                    <p className="text-[11px] text-slate-400">
                      {b.total_documents} docs · {fmtBytes(b.size_bytes)} · {b.exported_by === 'auto_backup' ? 'automatic' : (b.exported_by || 'manual')}
                    </p>
                  </div>
                </div>
                <button onClick={() => handleDownloadBackup(b)} data-testid={`download-backup-${b.backup_id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 rounded-sm text-slate-700 text-xs font-semibold hover:bg-slate-50 whitespace-nowrap">
                  <Download size={13} /> Download
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Import Result */}
      {importResult && (
        <div className="bg-white border border-emerald-200 rounded-sm shadow-sm p-6" data-testid="import-result">
          <div className="flex items-center gap-2.5 mb-3">
            <CheckCircle size={18} className="text-emerald-600" />
            <h3 className="font-heading font-bold text-emerald-800">{importResult.message}</h3>
          </div>
          {importResult.source_export_date && (
            <p className="text-xs text-slate-500 mb-3">Backup from: {new Date(importResult.source_export_date).toLocaleString()}</p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(importResult.details || {}).map(([key, { imported }]) => (
              <div key={key} className="bg-emerald-50 border border-emerald-100 rounded-sm px-3 py-2">
                <p className="text-[10px] font-bold uppercase text-emerald-600">{COLL_LABELS[key]?.label || key}</p>
                <p className="font-mono font-bold text-lg text-emerald-800">{imported}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Migration Instructions */}
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-5">
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-bold text-amber-800 text-sm mb-1.5">How to migrate data to a new deployment</h4>
            <ol className="text-sm text-amber-700 space-y-1.5 list-decimal list-inside">
              <li><strong>Export</strong> — Click "Download Backup" above to save your data as JSON</li>
              <li><strong>Deploy</strong> — Deploy the app to production</li>
              <li><strong>Login</strong> — Sign in to the deployed app with the admin account</li>
              <li><strong>Import</strong> — Go to Settings and click "Upload Backup" with the JSON file</li>
            </ol>
            <p className="text-xs text-amber-600 mt-2 italic">All parts, materials, customers, alerts, and templates will be restored.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
